import { Component, Input, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  Event,
  MerchantAccountTag,
  OneDayItem,
  OneDayMembership,
  Profile,
} from 'src/app/core/models';
import {
  OneDayService,
  ProfileService,
  ShopifyService,
  TransactionService,
} from 'src/app/core/services';
import { NonEnrollmentWaiverModalComponent } from 'src/app/shared/non-enrollment-waiver-modal/non-enrollment-waiver-modal.component';
import { environment } from 'src/environments/environment';
export interface OrderItem {
  label: string;
  price: number;
}

@Component({
  selector: 'app-one-day-license-cart',
  templateUrl: './one-day-license-cart.component.html',
  styleUrls: ['./one-day-license-cart.component.scss'],
})
export class OneDayLicenseCartComponent implements OnInit {
  @Input() selectedOneDays: OneDayItem[];
  @Input() user: Profile;
  @Input() event: Event;
  @Input() experienceRequired: boolean;

  isLoading = false;
  isSubmitting = false;

  serverError: any = null;
  waiverId: number;
  oneDayMemberships: OneDayMembership[] = [];
  usingNewApiVersion: boolean = environment.shopifyApiVersion >= '2025-01';

  get ghostModeActive(): boolean {
    return this.profileService.hasGhostModeCookie;
  }

  constructor(
    private modal: NgbModal,
    private oneDayService: OneDayService,
    private profileService: ProfileService,
    private shopifyService: ShopifyService,
    private transactionService: TransactionService
  ) {}

  ngOnInit(): void {}

  openWaiverModal(): void {
    if (!this.ghostModeActive) {
      const modal = this.modal.open(NonEnrollmentWaiverModalComponent, {
        centered: true,
        size: 'xl',
      });
      modal.componentInstance.user = this.user;
      modal.componentInstance.event = this.event;
      modal.componentInstance.eventDateIds = this.eventDateIds;
      modal.componentInstance.waiverId.subscribe((waiverId: number) => {
        this.waiverId = waiverId;
      });
      modal.result.then(() => this.createOneDays()).catch(() => {});
    } else {
      this.waiverId = null;
      this.createOneDays();
    }
  }

  createOneDays(): void {
    this.isSubmitting = true;
    let oneDaysCreated = 0;

    this.selectedOneDays.forEach((oneDayItem) => {
      const oneDay: OneDayMembership = {
        event_id: this.event.event_id,
        permit_id: this.event.event_permit_id,
        event_date_id: oneDayItem.eventDate.event_date_id,
        event_discipline_id: this.event.event_discipline,
        profile_id: this.user.profile_id,
        first_name: this.user.profile_first_name,
        last_name: this.user.profile_last_name,
        email: this.user.profile_email,
        experience_required: this.experienceRequired,
        signed_waiver_id: this.waiverId,
      };

      this.oneDayService.createOneDayMembership(oneDay).subscribe((membership) => {
        if (membership) {
          this.oneDayMemberships.push(membership);
          oneDaysCreated += 1;
          if (oneDaysCreated === this.selectedOneDays.length) {
            this.checkout();
          }
        } else {
          // TODO: @Charlie, @Brennan - Handle one day creation error
        }
      });
    });
  }

  checkout(): void {
    this.serverError = null;

    const customAttributes: Array<any> = [
      {
        key: 'source',
        value: 'membership',
      },
      {
        key: 'return_url',
        value: environment.eventSearchUrl,
      },
      {
        key: 'one_day_ids',
        value: this.oneDayIds,
      },
    ];

    const note = `membership, one-day-membership, ${MerchantAccountTag.Membership}`;

    const lineItems: Array<any> = [];

    this.selectedOneDays.forEach((oneDay) => {
      const existingItem = lineItems.find((item) => item.variantId === oneDay.variant.id);
      if (existingItem) {
        existingItem.quantity += 1;
      } else {
        lineItems.push({ variantId: oneDay.variant.id, quantity: 1 });
      }
    });

    if (this.usingNewApiVersion) {
      this.shopifyService
        .createCheckoutNew(lineItems, customAttributes, note)
        .then((checkoutModel) => {
          const transaction = { shopify_uuid: checkoutModel.cart.id };

          return new Promise((resolve, reject) => {
            this.transactionService
              .createStandardTransaction(transaction)
              .pipe(
                catchError((err) => {
                  this.isSubmitting = false;
                  this.serverError = err;
                  return throwError(err);
                })
              )
              .subscribe(
                () => {
                  window.location.href = this.transactionService.getCartUrl(checkoutModel.cart.id, {
                    email: this.user.profile_email,
                    firstName: this.user.profile_first_name,
                    lastName: this.user.profile_last_name,
                  });
                  resolve();
                },
                () => {
                  reject();
                }
              );
          });
        });
    } else {
      this.shopifyService
        .createCheckoutOld(lineItems, customAttributes, note)
        .then((checkoutModel) => {
          const transaction = { shopify_uuid: checkoutModel.checkout.id };

          return new Promise((resolve, reject) => {
            this.transactionService
              .createStandardTransaction(transaction)
              .pipe(
                catchError((err) => {
                  this.isSubmitting = false;
                  this.serverError = err;
                  return throwError(err);
                })
              )
              .subscribe(
                () => {
                  window.location.href = this.transactionService.getCheckoutUrl(
                    checkoutModel.checkout.id,
                    {
                      email: this.user.profile_email,
                      firstName: this.user.profile_first_name,
                      lastName: this.user.profile_last_name,
                    }
                  );
                  resolve();
                },
                () => {
                  reject();
                }
              );
          });
        });
    }
  }

  get total(): number {
    let total = 0;
    this.selectedOneDays.forEach(
      (oneDay) => (total += parseFloat(oneDay.variant.priceV2.amount) * oneDay.quantity)
    );
    return total;
  }

  get eventDateIds(): number[] {
    return this.selectedOneDays.map((oneDayItem) => oneDayItem.eventDate.event_date_id);
  }

  get oneDayIds(): string {
    return this.oneDayMemberships.map((membership) => membership.one_day_id).join(', ');
  }
}
